/**
 * Phạm vi chi nhánh của những gì màn hình đang hiển thị (#19869 / STAT-01b).
 *
 * <p>Từ #19842 mọi `/reports/*` tính theo phạm vi của request: có header `X-Addy-Branch-Id` → CHỈ
 * chi nhánh đó; không có header → các chi nhánh được gán trong `user_branch_access`, và không có
 * dòng gán nào nghĩa là MỌI chi nhánh (`ReportScope.branches()` trả `null` = không lọc). Header chỉ
 * được gửi khi `localStorage['addy.branchId']` có giá trị (`shared/api/client.ts`).
 *
 * <p>Nên "chưa ghim chi nhánh" là một phạm vi thật, có tên, và trước đây không ai nói ra nó: bộ
 * chọn hiện `branches[0]` như đang chọn trong khi báo cáo đang cộng cả phòng khám. Luật ở đây là
 * một hàm thuần để kiểm được không cần mở Radix Select (mở một lần ~15s trong jsdom).
 *
 * <p><b>Vì sao cần cả `accessible`:</b> `/v1/branches` trả MỌI chi nhánh của phòng khám, không lọc
 * theo quyền — một lễ tân chỉ được gán chi nhánh A vẫn thấy B trong danh sách, chọn vào thì server
 * trả 403 `branch_forbidden`. Claim `branches` của JWT là tập chi nhánh người đó được phép, nên
 * giao hai tập lại vừa bỏ được lựa chọn chắc chắn bị từ chối, vừa trả lời đúng câu hỏi quyết định
 * nhãn: "bỏ ghim thì có ra TOÀN phòng khám hay chỉ các chi nhánh của tôi?".
 *
 * <p>Claim không phân biệt được "không bị giới hạn" với "được gán đúng tất cả chi nhánh"
 * (`BranchAccessResolver` ghi danh sách tường minh cho cả hai) — và không cần phân biệt: cả hai
 * trường hợp bỏ ghim đều ra mọi chi nhánh, nên nhãn "tất cả chi nhánh" đúng cho cả hai.
 */

/** Giá trị của mục "Tất cả chi nhánh" trong `Select` — Radix không nhận chuỗi rỗng làm value. */
export const ALL_BRANCHES = "__all__";

/** Nhãn khi phạm vi là toàn bộ phòng khám. */
const LABEL_ALL = "tất cả chi nhánh";
/** Nhãn khi người xem bị giới hạn: "tất cả" của họ nhỏ hơn "tất cả" của phòng khám. */
const LABEL_ASSIGNED = "tất cả chi nhánh được giao";
/** Có ghim mà chưa biết tên (danh sách chi nhánh đang tải, hoặc người xem không có `clinic.read`). */
const LABEL_PINNED_UNKNOWN = "chi nhánh đang chọn";

export interface BranchOption {
  id: string;
  name: string;
}

export interface BranchScopeInput {
  /** `localStorage['addy.branchId']` — rỗng/null nghĩa là chưa ghim chi nhánh nào. */
  stored: string | null;
  /** Chi nhánh ACTIVE của phòng khám, theo thứ tự `/v1/branches` trả về. Rỗng = chưa biết. */
  active: BranchOption[];
  /** Chi nhánh người xem được phép (claim `branches` của JWT); `null` = không đọc được. */
  accessible: string[] | null;
}

export interface BranchScope {
  /** Chi nhánh người xem được phép ghim, theo thứ tự của `/v1/branches`. */
  options: BranchOption[];
  /** Chi nhánh đang ghim — chính giá trị đi vào header, hoặc `null` khi không gửi header. */
  pinnedId: string | null;
  /** Bỏ ghim có nghĩa là TOÀN phòng khám (không bị giới hạn chi nhánh). */
  coversEveryBranch: boolean;
  /** Nhãn của mục bỏ ghim trong bộ chọn. */
  allOptionLabel: string;
  /** Phạm vi đang hiển thị, dùng cho câu "Đang xem: …". */
  label: string;
  /** `value` cho `Select`. */
  value: string;
  /** Có gì để chuyển hay không — một chi nhánh thì bộ chọn chỉ là tiếng ồn. */
  showSwitcher: boolean;
  /** Có cần nói ra phạm vi hay không — phòng khám một chi nhánh thì mọi số đều là số duy nhất. */
  showNotice: boolean;
}

/**
 * Luật chọn/hiển thị phạm vi chi nhánh. Thuần: không đọc `localStorage`, không gọi mạng.
 *
 * <p>`pinnedId` lấy ĐÚNG giá trị đã lưu, kể cả khi không tìm được trong `active`. Giá trị đó là thứ
 * đang được gửi lên server, nên đổi nó thành `null` cho "gọn" là tự nói sai về phạm vi; không biết
 * tên thì nói là không biết tên.
 */
export function resolveBranchScope({ stored, active, accessible }: BranchScopeInput): BranchScope {
  const pinnedId = stored && stored.trim() ? stored.trim() : null;
  const allowed = accessible == null ? null : new Set(accessible);
  const narrowed = allowed ? active.filter((b) => allowed.has(b.id)) : active;
  // Claim có thể lạc hậu (claim chỉ nạp lại ở lần đăng nhập sau, xem ReportScope): nếu nó không
  // giao với chi nhánh ACTIVE nào thì coi như không biết, chứ không dựng một bộ chọn rỗng.
  const options = narrowed.length > 0 ? narrowed : active;
  const coversEveryBranch = options.length === active.length;
  const pinnedName = active.find((b) => b.id === pinnedId)?.name ?? null;

  let label: string;
  if (pinnedId) {
    label = pinnedName ?? LABEL_PINNED_UNKNOWN;
  } else if (coversEveryBranch) {
    label = LABEL_ALL;
  } else if (options.length === 1) {
    // Bị giới hạn đúng một chi nhánh: bỏ ghim vẫn chỉ ra chi nhánh đó, nên nói tên nó mới là đúng.
    label = options[0].name;
  } else {
    label = LABEL_ASSIGNED;
  }

  return {
    options,
    pinnedId,
    coversEveryBranch,
    allOptionLabel: coversEveryBranch ? "Tất cả chi nhánh" : "Tất cả chi nhánh được giao",
    label,
    value: pinnedId ?? ALL_BRANCHES,
    showSwitcher: options.length > 1,
    showNotice: pinnedId !== null || active.length > 1,
  };
}

/**
 * Claim `branches` trong access token, hoặc `null` khi không có token / không đọc được.
 *
 * <p>Chỉ dùng để quyết định màn hình MỜI gì; mọi endpoint vẫn tự kiểm quyền của nó (cùng cách
 * `/v1/me` trả `permissions` để ẩn menu). Không xác thực chữ ký — việc đó là của server.
 */
export function decodeBranchClaim(token: string | null | undefined): string[] | null {
  if (!token) {
    return null;
  }
  const payload = token.split(".")[1];
  if (!payload) {
    return null;
  }
  try {
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (ch) => ch.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as { branches?: unknown };
    if (!Array.isArray(claims.branches)) {
      return null;
    }
    const ids = claims.branches.filter((v): v is string => typeof v === "string");
    return ids.length > 0 ? ids : null;
  } catch {
    return null;
  }
}
